import { Gavel } from 'lucide-react';
import { z } from 'zod';
import { Button } from './Button';
import { Surface } from './Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { FieldChecklist, type FieldChecklistItem } from './FieldChecklist';
import { Form, FormField, type ZodForm } from './Form';
import { Input, Textarea } from './Input';
import { Grid, Inline, Stack } from './Layout';
import { PhotoPickerGrid } from './PhotoPickerGrid';
import { Select, type SelectOption } from './Select';
import { Text } from './Text';

const FORM_ID = 'move-to-auction-form';

export type MoveToAuctionPriceType = 'FIXED' | 'STARTING_BID';

const PRICE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'FIXED', label: 'Fixed price' },
  { value: 'STARTING_BID', label: 'Starting bid' },
];

export const moveToAuctionSchema = z.object({
  price: z
    .number({
      required_error: 'Set a price — the listing goes live with it.',
      invalid_type_error: 'Set a price — the listing goes live with it.',
    })
    .min(0, 'Price can’t be negative.'),
  priceType: z.enum(['FIXED', 'STARTING_BID']).default('FIXED'),
  titleStatus: z.string(),
  conditionNotes: z.string(),
});

export type MoveToAuctionValues = z.infer<typeof moveToAuctionSchema>;

export interface MoveToAuctionDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the lot will be called, following the ticked fields. */
  listingTitle: string;
  /** The source's vehicle facts, with whether each carries over. */
  fields: FieldChecklistItem[];
  onToggleField: (key: string) => void;
  /** The source's photos and the ticked subset. */
  photos: string[];
  selectedPhotos: string[];
  onTogglePhoto: (url: string) => void;
  /** From `useZodForm(moveToAuctionSchema, …)`. */
  form: ZodForm<typeof moveToAuctionSchema>;
  /** The lot is being created: both footer buttons lock. */
  saving?: boolean;
  onConfirm: (values: MoveToAuctionValues) => void;
}

/**
 * "Move to auction" from a call or an impound: a preview of the listing
 * title, the facts and photos that carry over, then price, price type,
 * title status and condition notes.
 *
 *   <MoveToAuctionDialogView open={open} onOpenChange={setOpen}
 *     listingTitle="2019 Ford F-150" fields={fields} onToggleField={toggleField}
 *     photos={source.photos} selectedPhotos={[...chosen]} onTogglePhoto={togglePhoto}
 *     form={form} saving={createLot.isPending} onConfirm={confirm} />
 */
export function MoveToAuctionDialogView({
  open,
  onOpenChange,
  listingTitle,
  fields,
  onToggleField,
  photos,
  selectedPhotos,
  onTogglePhoto,
  form,
  saving = false,
  onConfirm,
}: MoveToAuctionDialogViewProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <Inline as="span" gap={2} align="center">
              <Gavel className="size-icon-md" />
              Move to auction
            </Inline>
          </DialogTitle>
          <DialogDescription>
            Pick what carries over. The vehicle goes live on your public auction
            page as soon as you confirm.
          </DialogDescription>
        </DialogHeader>

        <Stack gap={6}>
          <Surface radius="lg" tone="muted" padding="sm">
            <Text variant="caption" tone="muted">
              Will be listed as
            </Text>
            <Text variant="body-strong">{listingTitle}</Text>
          </Surface>

          <FieldChecklist
            label="Vehicle details to include"
            items={fields}
            onToggle={onToggleField}
          />

          <PhotoPickerGrid
            photos={photos}
            selected={selectedPhotos}
            onToggle={onTogglePhoto}
          />

          <Form form={form} gap={6} id={FORM_ID} onSubmit={onConfirm}>
            <Grid cols={{ base: 1, sm: 2 }} gap={4}>
              <FormField form={form} name="price" label="Price (USD)">
                {(field) => (
                  <Input
                    type="number"
                    min={0}
                    placeholder="12000"
                    name={field.name}
                    ref={field.ref}
                    onBlur={field.onBlur}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                  />
                )}
              </FormField>
              <FormField form={form} name="priceType" label="Price type">
                {(field) => (
                  <Select
                    options={PRICE_TYPE_OPTIONS}
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>
            </Grid>

            <FormField form={form} name="titleStatus" label="Title status">
              {(field) => <Input {...field} />}
            </FormField>

            <FormField
              form={form}
              name="conditionNotes"
              label="Condition notes"
            >
              {(field) => (
                <Textarea
                  {...field}
                  rows={3}
                  placeholder="Front-end collision damage; engine turns over. Interior in fair condition."
                />
              )}
            </FormField>
          </Form>
        </Stack>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button form={FORM_ID} type="submit" disabled={saving}>
            {saving ? 'Moving…' : 'Move & go live'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
